<template>
  <div>
    <vue-element-loading
      :active="loading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#005f32"
      spinner="bar-fade-scale"
    />
    <v-snackbar v-model="snackbar" :color="snackbarColor" right top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>

    <v-layout wrap>
      <v-flex xs12>
        <v-card-title class="elevation-0 px-0">
          <span class="itemHeading" style="color: #005f32">Manage Troubleshooting Tickets (Admin)</span>
        </v-card-title>

        <v-layout wrap justify-start mt-4>
          <v-flex xs12>
            <v-card class="elevation-2 rounded-lg overflow-hidden">
              <v-data-table
                :headers="headers"
                :items="tickets"
                :loading="loading"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="elevation-0 custom-ticket-table"
                @page-count="pageCount = $event"
                :search="search"
              >
                <template v-slot:top>
                  <v-text-field
                    v-model="search"
                    label="Search by Employee, ID or Status"
                    class="mx-4 mt-4 rounded-xl"
                    outlined
                    dense
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    color="#005f32"
                  ></v-text-field>
                </template>

                <!-- Employee Custom Slot -->
                <template v-slot:item.uid="{ item }">
                  <div v-if="item.uid" class="kumbhMedium">
                    {{ item.uid.name }}<br/>
                    <small class="grey--text">{{ item.uid.employeeId }}</small>
                  </div>
                  <span v-else>-</span>
                </template>

                <!-- Attendance Date Custom Slot (Commented out for now) -->
                <!-- <template v-slot:item.attendanceDate="{ item }">
                  <span v-if="item.attendanceDate">
                    {{ formatDate(item.attendanceDate) }}
                  </span>
                  <v-chip v-else x-small outlined color="grey">System Bug</v-chip>
                </template> -->

                <!-- Status Custom Slot -->
                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="getStatusColor(item.status)"
                    dark
                    class="kumbhBold px-4"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <!-- Reported On Custom Slot -->
                <template v-slot:item.create_date="{ item }">
                  <span class="kumbhMedium">{{ formatDateTime(item.create_date) }}</span>
                </template>

                <!-- Actions Slot -->
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    color="#005f32"
                    @click="openActionDialog(item)"
                    title="Review & Update"
                  >
                    <v-icon>mdi-square-edit-outline</v-icon>
                  </v-btn>
                </template>
              </v-data-table>

              <!-- Custom Pagination -->
              <v-layout wrap justify-center py-6 v-if="tickets.length > 0">
                <v-pagination
                  v-model="page"
                  :length="pageCount"
                  :total-visible="7"
                  class="custom-pagination"
                  color="#005f32"
                ></v-pagination>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- Review Dialog (Admin) -->
    <v-dialog v-model="actionDialog" max-width="650px" scrollable>
      <v-card v-if="selectedTicket" class="rounded-lg">
        <v-toolbar color="#005f32" dark flat dense>
          <v-toolbar-title class="kumbhBold">Review Ticket: {{ selectedTicket.uid ? selectedTicket.uid.name : 'Unknown' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="actionDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-layout wrap>
            <!-- Header Info (Styled) -->
            <v-flex xs12 mb-3>
              <v-card flat class="pa-2 rounded-lg" style="background: #f1f8f5; border: 1px solid #e1eee7;">
                <v-layout align-center wrap>
                  <v-flex xs5>
                    <v-icon small color="#005f32" class="mr-1">mdi-shield-check-outline</v-icon>
                    <span class="grey--text text--darken-3 kumbhBold mr-2" style="font-size: 11px; text-transform: uppercase;">Current Status:</span>
                    <v-chip x-small :color="getStatusColor(selectedTicket.status)" dark class="kumbhBold px-3 elevation-1">
                      {{ selectedTicket.status }}
                    </v-chip>
                  </v-flex>
                  <v-flex xs7 text-right>
                    <v-icon small color="grey darken-1" class="mr-1">mdi-clock-outline</v-icon>
                    <span class="grey--text text--darken-3 kumbhBold mr-1" style="font-size: 11px; text-transform: uppercase;">Reported On:</span>
                    <span class="kumbhMedium" style="font-size: 12px; color: #333;">{{ formatDateTime(selectedTicket.create_date) }}</span>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>

            <!-- Issue Description -->
            <v-flex xs12 mb-3>
              <div class="d-flex align-center mb-2">
                <v-icon small color="#005f32" class="mr-2">mdi-text-box-outline</v-icon>
                <span class="grey--text text--darken-3 kumbhBold" style="font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase;">Issue Description</span>
              </div>
              <div class="kumbhRegular white pa-3 rounded-lg" style="white-space: pre-wrap; font-size: 14px; border: 1px solid #ddd; box-shadow: 0 2px 4px rgba(0,0,0,0.02); color: #444; line-height: 1.5;">
                {{ selectedTicket.description }}
              </div>
            </v-flex>

            <!-- Screenshots Gallery -->
            <v-flex xs12 mb-3 v-if="selectedTicket.screenshots && selectedTicket.screenshots.length > 0">
              <div class="d-flex align-center mb-2">
                <v-icon small color="#005f32" class="mr-2">mdi-image-multiple-outline</v-icon>
                <span class="grey--text text--darken-3 kumbhBold" style="font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase;">Attached Evidence</span>
              </div>
              <v-layout wrap>
                <v-flex v-for="(img, idx) in selectedTicket.screenshots" :key="idx" xs3 sm2 pa-1>
                  <v-hover v-slot="{ hover }">
                    <v-card flat outlined class="rounded-lg overflow-hidden cursor-pointer transition-swing" 
                            :class="hover ? 'elevation-4' : 'elevation-0'"
                            @click="openImage(img)" 
                            style="border: 1px solid #ddd;">
                      <v-img :src="mediaURL + img" aspect-ratio="1" cover>
                         <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="#005f32" size="20"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </v-card>
                  </v-hover>
                </v-flex>
              </v-layout>
            </v-flex>

            <v-divider class="xs12 mb-3"></v-divider>

            <!-- Admin Action Area (Styled as a distinct section) -->
            <v-flex xs12 mt-1 pa-3 rounded-lg style="background: #f9f9f9; border: 1px solid #eee;">
              <div class="d-flex align-center mb-3">
                <v-icon small color="#005f32" class="mr-2">mdi-gavel</v-icon>
                <span class="kumbhBold" style="color: #005f32; font-size: 13px; text-transform: uppercase; letter-spacing: 1px;">Admin Action & Resolution</span>
              </div>
              <v-layout wrap>
                <v-flex xs12 sm4 pr-sm-2 mb-2>
                  <v-select
                    v-model="updateData.status"
                    :items="['Under Review', 'Resolved', 'Rejected']"
                    label="Update Status"
                    outlined
                    dense
                    hide-details
                    class="rounded-lg kumbhRegular white"
                    color="#005f32"
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm8 mb-2>
                  <v-textarea
                    v-model="updateData.adminRemark"
                    label="Resolution Note / Remarks"
                    outlined
                    dense
                    rows="2"
                    hide-details
                    class="rounded-lg kumbhRegular white"
                    color="#005f32"
                    placeholder="Provide details about the resolution or rejection..."
                  ></v-textarea>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text class="kumbhMedium px-4" @click="actionDialog = false">Cancel</v-btn>
          <v-btn
            color="#005f32"
            dark
            class="px-8 rounded-lg kumbhBold white--text elevation-2"
            :loading="updating"
            @click="updateStatus"
          >
            Update Ticket
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "TroubleshootAdmin",
  data() {
    return {
      loading: false,
      updating: false,
      search: "",
      tickets: [],
      page: 1,
      itemsPerPage: 15,
      pageCount: 0,
      selectedTicket: null,
      actionDialog: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      updateData: {
        status: "",
        adminRemark: ""
      },
      headers: [
        { text: "Employee", value: "uid", class: "white--text font-weight-bold" },
        { text: "Description", value: "description", class: "white--text font-weight-bold", width: "300" },
        // { text: "Attendance Date", value: "attendanceDate", class: "white--text font-weight-bold" },
        { text: "Status", value: "status", class: "white--text font-weight-bold" },
        { text: "Reported On", value: "create_date", class: "white--text font-weight-bold" },
        { text: "Actions", value: "actions", sortable: false, align: "right", class: "white--text font-weight-bold" },
      ],
    };
  },
  mounted() {
    this.getAllTickets();
  },
  methods: {
    async getAllTickets() {
      this.loading = true;
      try {
        const response = await axios({
          method: "GET",
          url: "/troubleshoot/admin/all-tickets",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        });
        if (response.data.status) {
          this.tickets = response.data.data || [];
        }
      } catch (error) {
        console.error("Error fetching admin tickets:", error);
      } finally {
        this.loading = false;
      }
    },
    async updateStatus() {
      if (!this.updateData.status) return;

      this.updating = true;
      try {
        const response = await axios({
          method: "PUT",
          url: "/troubleshoot/admin/status",
          data: {
            ticketId: this.selectedTicket._id,
            status: this.updateData.status,
            adminRemark: this.updateData.adminRemark
          },
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        });

        if (response.data.status) {
          this.showSnackbar(response.data.msg, "success");
          this.actionDialog = false;
          this.getAllTickets();
        } else {
          this.showSnackbar(response.data.msg, "error");
        }
      } catch (error) {
        this.showSnackbar("Failed to update status", "error");
      } finally {
        this.updating = false;
      }
    },
    openActionDialog(item) {
      this.selectedTicket = item;
      this.updateData.status = item.status;
      this.updateData.adminRemark = item.adminRemark || "";
      this.actionDialog = true;
    },
    getStatusColor(status) {
      switch (status) {
        case "Open": return "blue";
        case "Under Review": return "orange";
        case "Resolved": return "green";
        case "Rejected": return "red";
        default: return "grey";
      }
    },
    formatDate(date) {
        if (!date) return "";
        return moment(date).format("DD/MM/YYYY");
    },
    formatDateTime(date) {
        if (!date) return "";
        return moment(date).utcOffset("+05:30").format("DD/MM/YYYY, HH:mm:ss");
    },
    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color === "success" ? "#005f32" : "error";
      this.snackbar = true;
    },
    openImage(key) {
      window.open(this.mediaURL + key, "_blank");
    }
  },
};
</script>

<style scoped>
.itemHeading {
  font-family: 'KumbhBold';
  font-size: 24px;
}
.kumbhRegular {
  font-family: 'KumbhRegular';
}
.kumbhMedium {
  font-family: 'KumbhMedium';
}
.kumbhBold {
  font-family: 'KumbhBold';
}

.custom-ticket-table >>> thead {
  background-color: #005f32 !important;
}

.custom-ticket-table >>> th {
  font-family: "kumbhMedium" !important;
  font-size: 15px !important;
  color: #ffffff !important;
}

.custom-ticket-table >>> td {
  font-family: "kumbhMedium" !important;
  font-size: 14px !important;
}
</style>
